/**
 * 💜 Cálculos de Sinastría Numerológica - Versión Simplificada
 * 
 * Calcula la compatibilidad numerológica entre dos personas
 * basándose en sus números personales (día de nacimiento reducido).
 * 
 * @version 2.0 - 2026-02-02
 */

// ============================================
// FUNCIONES DE REDUCCIÓN NUMEROLÓGICA
// ============================================

/**
 * Suma los dígitos de un número
 */
const sumDigits = (n) => Math.abs(n).toString().split('').reduce((a, b) => a + Number(b), 0);

/**
 * Reduce un número a un solo dígito, respetando números maestros (11, 22)
 */
export const reduceNumber = (n) => {
  let x = Math.abs(n);
  while (x >= 10 && x !== 11 && x !== 22) {
    x = sumDigits(x);
  }
  return x;
};

/**
 * Parsea una fecha en formato YYYY-MM-DD y retorna {day, month, year}
 */
export const parseDate = (dateString) => {
  if (!dateString) return null;
  
  const date = new Date(dateString + 'T00:00:00');
  if (isNaN(date.getTime())) return null;
  
  return {
    day: date.getDate(),
    month: date.getMonth() + 1,
    year: date.getFullYear()
  };
};

/**
 * Calcula el Número Personal (día de nacimiento reducido)
 * Es la "B" del pináculo
 */
export const calculatePersonalNumber = (birthDate) => {
  const parsed = typeof birthDate === 'string' ? parseDate(birthDate) : birthDate;
  if (!parsed) return null;
  return reduceNumber(parsed.day);
};

// ============================================
// TABLA DE COMPATIBILIDAD
// ============================================

/**
 * Tabla de compatibilidad numerológica
 * Para cada número personal, define qué números son:
 * - natural: Pareja Natural
 * - compatible: Pareja Compatible  
 * - challenge: Pareja Desafío
 * - neutral: Pareja Neutral
 */
export const COMPATIBILITY_TABLE = {
  1: {
    natural: [1, 5, 7, 11],
    compatible: [2, 3, 9],
    challenge: [4, 6, 22],
    neutral: [8]
  },
  2: {
    natural: [2, 4, 8, 22],
    compatible: [1, 3, 6, 9],
    challenge: [5, 7, 11],
    neutral: []
  },
  3: {
    natural: [3, 6, 9],
    compatible: [1, 2, 5],
    challenge: [4, 7, 8, 11, 22],
    neutral: []
  },
  4: {
    natural: [2, 4, 8, 22],
    compatible: [6, 7, 11],
    challenge: [1, 3, 5, 9],
    neutral: []
  },
  5: {
    natural: [1, 5, 7, 11],
    compatible: [3, 9],
    challenge: [2, 4, 6, 22],
    neutral: [8]
  },
  6: {
    natural: [3, 6, 9],
    compatible: [2, 4, 8, 22],
    challenge: [1, 5, 7, 11],
    neutral: []
  },
  7: {
    natural: [1, 5, 7, 11],
    compatible: [4, 22],
    challenge: [2, 3, 6, 8, 9],
    neutral: []
  },
  8: {
    natural: [2, 4, 8, 22],
    compatible: [6],
    challenge: [3, 7, 9, 11],
    neutral: [1, 5]
  },
  9: {
    natural: [3, 6, 9],
    compatible: [1, 2, 5],
    challenge: [4, 7, 8, 11, 22],
    neutral: []
  },
  11: {
    natural: [1, 5, 7, 11],
    compatible: [4, 8, 22],
    challenge: [2, 3, 6, 9],
    neutral: []
  },
  22: {
    natural: [2, 4, 8, 22],
    compatible: [6, 7, 11],
    challenge: [1, 3, 5, 9],
    neutral: []
  }
};

// ============================================
// GRUPOS DE NÚMEROS
// ============================================

export const NUMBER_GROUPS = {
  cerebral: {
    numbers: [1, 5, 7, 11],
    name: 'Grupo Cerebral',
    emoji: '🧠',
    description: 'Números Mentales. Siempre están pensando, procesan cada aspecto de la vida mentalmente. Buscan encontrar a alguien intelectualmente semejante y se estimulan mediante ideas y conversación. Disfrutan el movimiento y la aventura.',
    inRelationship: 'En pareja, los Cerebrales necesitan estimulación intelectual constante. Valoran las conversaciones profundas y la libertad mental. Pueden parecer distantes emocionalmente, pero su amor se demuestra compartiendo ideas, planes y aventuras. Necesitan una pareja que respete su espacio para pensar y que pueda seguirles el ritmo mental.',
    strengths: ['Conversaciones estimulantes', 'Independencia saludable', 'Visión innovadora', 'Resolución de problemas'],
    challenges: ['Tendencia a racionalizar emociones', 'Pueden parecer fríos', 'Necesidad de espacio personal']
  },
  material: {
    numbers: [2, 4, 8, 22],
    name: 'Grupo Material',
    emoji: '💼',
    description: 'Números orientados hacia los negocios, siempre involucrados en acciones materiales y prácticas. Son realistas, aterrizados, dan ubicación y arraigo. Buscan crear una base de protección económica sólida.',
    inRelationship: 'En pareja, los Materiales son la roca de la relación. Demuestran amor a través de acciones: proveer, proteger y construir un hogar seguro. Son leales, comprometidos y trabajan duro por el bienestar conjunto. Buscan estabilidad y planificación a largo plazo. Su amor es práctico pero profundo.',
    strengths: ['Lealtad inquebrantable', 'Estabilidad emocional y material', 'Compromiso a largo plazo', 'Acciones sobre palabras'],
    challenges: ['Resistencia al cambio', 'Pueden ser tercos', 'Dificultad con la espontaneidad']
  },
  creative: {
    numbers: [3, 6, 9],
    name: 'Grupo Creativo',
    emoji: '🎨',
    description: 'Números creativos, asociados con las ideas y el idealismo. Les gusta tener un auditorio, que les pongan atención. Son expresivos y buscan conexiones emocionales profundas.',
    inRelationship: 'En pareja, los Creativos son románticos apasionados. Viven el amor intensamente y necesitan expresar y recibir afecto constantemente. Son detallistas, creativos para sorprender y muy comunicativos emocionalmente. Buscan una conexión del alma, no solo física. El romance para ellos es un arte.',
    strengths: ['Romanticismo genuino', 'Expresión emocional', 'Creatividad en el amor', 'Empatía profunda'],
    challenges: ['Hipersensibilidad', 'Necesidad de validación', 'Pueden dramatizar situaciones']
  }
};

// ============================================
// SIGNIFICADOS DE GRUPOS EN PAREJA
// ============================================

/**
 * Qué significa cuando ambos son del mismo grupo
 */
export const SAME_GROUP_MEANINGS = {
  cerebral: {
    title: '🧠 Dos Mentes Brillantes',
    description: 'Ambos procesan la vida de forma mental e intelectual. Se entienden sin muchas palabras porque piensan de manera similar. Las conversaciones serán estimulantes y nunca aburridas. Pueden planear grandes aventuras juntos.',
    dynamic: 'Se estimulan intelectualmente y comparten la necesidad de libertad. El desafío es no competir entre sí por tener la razón, y aprender a conectar también desde las emociones.',
    advice: 'Reserven momentos para hablar de sentimientos, no solo de ideas. Eviten convertir las discusiones en debates donde alguien tiene que "ganar".'
  },
  material: {
    title: '💼 Cimientos Inquebrantables',
    description: 'Ambos valoran la estabilidad, la seguridad y construir algo duradero. Formarán un equipo sólido para lograr metas materiales y crear un hogar próspero. Son la pareja que "construye imperios juntos".',
    dynamic: 'Trabajan bien en equipo hacia objetivos comunes. El desafío es no volverse demasiado rígidos ni dejar que la rutina apague la chispa.',
    advice: 'Introduzcan pequeñas sorpresas en la rutina. Celebren los logros, no solo trabajen por ellos. Permítanse soñar más allá de lo práctico.'
  },
  creative: {
    title: '🎨 Almas Artísticas',
    description: 'Ambos viven desde el corazón y las emociones. La relación será intensa, romántica y llena de expresión. Se entienden emocionalmente de forma profunda y nunca faltarán gestos de amor.',
    dynamic: 'Hay mucha pasión y romanticismo. El desafío es no dejarse llevar solo por emociones y mantener cierta objetividad cuando surjan conflictos.',
    advice: 'Cuando discutan, den un paso atrás antes de reaccionar. Equilibren la intensidad con momentos de calma. No todo tiene que ser dramático.'
  }
};

/**
 * Qué significa cuando son de grupos diferentes
 */
export const DIFFERENT_GROUP_MEANINGS = {
  'cerebral-material': {
    title: '🧠💼 Visión + Ejecución',
    description: 'El Cerebral aporta ideas, estrategia e innovación. El Material aporta estructura, perseverancia y ejecución. Juntos pueden convertir sueños en realidad.',
    dynamic: 'El Cerebral inspira con visión; el Material construye con disciplina. Pueden frustarse mutuamente: el Cerebral quiere cambiar planes constantemente, el Material quiere seguir el camino trazado.',
    advice: 'El Cerebral debe aprender a comprometerse con un plan. El Material debe abrirse a nuevas ideas sin verlas como amenazas a la estabilidad.'
  },
  'cerebral-creative': {
    title: '🧠🎨 Razón + Corazón',
    description: 'El Cerebral vive desde la mente; el Creativo desde el corazón. Es la unión clásica de "opuestos que se atraen". Pueden equilibrarse mutuamente de forma hermosa.',
    dynamic: 'El Creativo puede ablandar al Cerebral y enseñarle a sentir. El Cerebral puede dar perspectiva al Creativo cuando las emociones nublen su juicio. El reto es que cada uno no invalide la forma de ser del otro.',
    advice: 'El Cerebral debe esforzarse en expresar emociones, aunque se sienta incómodo. El Creativo debe no tomarse personal cuando el otro racionalice.'
  },
  'material-creative': {
    title: '💼🎨 Estructura + Fluidez',
    description: 'El Material ofrece estabilidad, seguridad y tierra firme. El Creativo aporta color, emoción y alegría a la rutina. Juntos crean una vida bella Y sólida.',
    dynamic: 'El Creativo saca al Material de su zona de confort con sorpresas y espontaneidad. El Material da seguridad al Creativo para que pueda expresarse sin preocupaciones.',
    advice: 'El Material debe soltar el control a veces y dejarse sorprender. El Creativo debe valorar profundamente la estabilidad que recibe, no darla por sentada.'
  }
};

/**
 * Obtiene el significado de grupos diferentes
 */
export const getDifferentGroupMeaning = (group1Key, group2Key) => {
  if (!group1Key || !group2Key) return null;
  const key1 = `${group1Key}-${group2Key}`;
  const key2 = `${group2Key}-${group1Key}`;
  return DIFFERENT_GROUP_MEANINGS[key1] || DIFFERENT_GROUP_MEANINGS[key2] || null;
};

/**
 * Obtiene el grupo al que pertenece un número
 */
export const getNumberGroup = (num) => {
  for (const [key, group] of Object.entries(NUMBER_GROUPS)) {
    if (group.numbers.includes(num)) {
      return { key, ...group };
    }
  }
  return null;
};

// ============================================
// TIPOS DE PAREJA
// ============================================

export const COUPLE_TYPES = {
  natural: {
    name: 'Pareja Natural',
    emoji: '💚',
    color: 'green',
    shortDesc: 'Conexión instantánea y fluida',
    description: 'Esta combinación establece una relación instantánea. Pueden sentir lo que el otro piensa y siente desde el primer contacto. Cuando empiezan a hablar sobre su vida incluso podrán descubrir que han pasado por experiencias muy similares.',
    advice: 'Con frecuencia la Pareja Natural resulta no ser tan buena compañera cuando uno de los dos está vibrando el lado positivo del número mientras el otro está en la vibración negativa. Asegúrense de crecer juntos en la misma dirección.'
  },
  compatible: {
    name: 'Pareja Compatible',
    emoji: '💙',
    color: 'blue',
    shortDesc: 'Se complementan y aprenden uno del otro',
    description: 'Esta combinación funciona como una relación complemento. Se llevan muy bien y existe la posibilidad de apoyo mutuo y cooperación para lograr sus metas. Generalmente son parejas que aprenden uno del otro o se fortalecen mutuamente.',
    advice: 'Estas parejas vienen a enseñarnos a negociar y el arte de aprender a estar de acuerdo en el desacuerdo. La atracción estará presente, pero deberán trabajar conscientemente por la armonía, la paz y la felicidad en pareja.'
  },
  challenge: {
    name: 'Pareja Desafío',
    emoji: '🔥',
    color: 'orange',
    shortDesc: 'Requiere trabajo y paciencia',
    description: 'Esta combinación representará un gran reto para llevarse bien. Existirán malos entendidos, tendencia a que se rompa la comunicación entre ambos, y a que se obstaculicen mutuamente en el crecimiento individual.',
    advice: 'Vienen a trabajar la tolerancia, la paciencia, la fortaleza de espíritu y el entendimiento. Es como si hablaran dos idiomas distintos. A pesar de resultar frustrante, puede generar una gran pasión, pero siempre representará un trabajo por realizar.'
  },
  neutral: {
    name: 'Pareja Neutral',
    emoji: '⚪',
    color: 'gray',
    shortDesc: 'No se interfieren, respetan la individualidad',
    description: 'La pareja que resulta de una compatibilidad neutral no se interfiere el uno al otro. Cada uno ya tiene bien definida su postura en la vida y ha crecido hacia el entendimiento de dejar ser al otro y respetar su individualidad.',
    advice: 'Generalmente comienzan con una gran intensidad y pasión, ambos buscan obtener aquello que tanto anhelan. Les atrae la conquista del otro, pero una vez que se ha logrado el objetivo, la relación podría volverse fría si no cultivan la conexión conscientemente.'
  }
};

/**
 * Determina el tipo de pareja según los números personales
 */
export const getCoupleType = (num1, num2) => {
  const compatibility = COMPATIBILITY_TABLE[num1];
  if (!compatibility) return null;
  
  if (compatibility.natural.includes(num2)) return 'natural';
  if (compatibility.compatible.includes(num2)) return 'compatible';
  if (compatibility.challenge.includes(num2)) return 'challenge';
  if (compatibility.neutral.includes(num2)) return 'neutral';
  
  return null;
};

// ============================================
// FUNCIÓN PRINCIPAL DE CÁLCULO
// ============================================

/**
 * Calcula la sinastría simplificada entre dos personas
 * 
 * @param {Object} person1 - { name, birthDate: 'YYYY-MM-DD' }
 * @param {Object} person2 - { name, birthDate: 'YYYY-MM-DD' }
 * @returns {Object} - Resultados de sinastría
 */
export function calculateSynastry(person1, person2) {
  const date1 = parseDate(person1.birthDate);
  const date2 = parseDate(person2.birthDate);
  
  if (!date1 || !date2) {
    throw new Error('Fechas de nacimiento inválidas');
  }

  // Número Personal de cada uno (día de nacimiento reducido)
  const personalNumber1 = reduceNumber(date1.day);
  const personalNumber2 = reduceNumber(date2.day);
  
  // Número de Pareja (suma de ambos días reducida)
  const coupleNumber = reduceNumber(date1.day + date2.day);
  
  // Tipo de compatibilidad
  const coupleTypeKey = getCoupleType(personalNumber1, personalNumber2);
  const coupleType = coupleTypeKey ? COUPLE_TYPES[coupleTypeKey] : null;
  
  // Grupo de cada persona
  const group1 = getNumberGroup(personalNumber1);
  const group2 = getNumberGroup(personalNumber2);
  
  // ¿Son del mismo grupo?
  const sameGroup = group1?.key === group2?.key;

  // ============================================
  // CONEXIONES POR ASPECTOS (información adicional)
  // ============================================
  
  // Conexión Emocional (suma de meses)
  const emotionalConnection = reduceNumber(date1.month + date2.month);
  
  // Conexión Física/Material (suma de días) - ya es el coupleNumber
  const physicalConnection = coupleNumber;
  
  // Conexión Espiritual (suma de años)
  const spiritualConnection = reduceNumber(date1.year + date2.year);
  
  // ============================================
  // PINÁCULO DE PAREJA (A, B, C, D)
  // ============================================
  
  // Sumas crudas (antes de reducir) - necesarias para calcular D correctamente
  const rawMonthSum = date1.month + date2.month;
  const rawDaySum = date1.day + date2.day;
  const rawYearSum = date1.year + date2.year;
  
  // A = Karma (suma de meses, reducida)
  const pinnacleA = emotionalConnection; // = reduceNumber(rawMonthSum)
  // B = Pareja (suma de días, reducida)
  const pinnacleB = coupleNumber; // = reduceNumber(rawDaySum)
  // C = Antídoto (suma de años, reducida)
  const pinnacleC = spiritualConnection; // = reduceNumber(rawYearSum)
  // D = Imagen / Destino: se reduce la SUMA TOTAL CRUDA (no la suma de valores ya reducidos)
  // Esto preserva números maestros que se pierden al sumar valores ya reducidos
  const pinnacleD = reduceNumber(rawMonthSum + rawDaySum + rawYearSum);

  // Año Personal de la Pareja (para el año actual)
  const currentYear = new Date().getFullYear();
  const couplePersonalYear = reduceNumber(
    date1.month + date2.month + date1.day + date2.day + currentYear
  );

  // ============================================
  // VIBRACIÓN DOMINANTE
  // ============================================
  
  // Contamos qué grupo tiene más representación
  const allNumbers = [personalNumber1, personalNumber2, emotionalConnection, physicalConnection, spiritualConnection];
  const groupCounts = { cerebral: 0, material: 0, creative: 0 };
  
  allNumbers.forEach(num => {
    const g = getNumberGroup(num);
    if (g) groupCounts[g.key]++;
  });
  
  const dominantGroupKey = Object.entries(groupCounts).sort((a, b) => b[1] - a[1])[0][0];
  const dominantGroup = NUMBER_GROUPS[dominantGroupKey];

  return {
    // Datos de las personas
    person1: {
      name: person1.name || 'Tú',
      fullName: `${person1.name || ''} ${person1.lastName || ''} ${person1.scdLastName || ''}`.trim(),
      birthDate: person1.birthDate,
      birthDay: date1.day,
      birthMonth: date1.month,
      birthYear: date1.year,
      personalNumber: personalNumber1,
      group: group1
    },
    person2: {
      name: person2.name || 'Tu pareja',
      fullName: `${person2.name || ''} ${person2.lastName || ''} ${person2.scdLastName || ''}`.trim(),
      birthDate: person2.birthDate,
      birthDay: date2.day,
      birthMonth: date2.month,
      birthYear: date2.year,
      personalNumber: personalNumber2,
      group: group2
    },
    
    // Número de Pareja
    coupleNumber,
    
    // Tipo de compatibilidad
    coupleTypeKey,
    coupleType,
    
    // Grupos
    sameGroup,
    
    // Conexiones por aspecto
    connections: {
      emotional: {
        value: emotionalConnection,
        label: 'Conexión Emocional',
        emoji: '💗',
        description: 'Cómo se conectan emocionalmente'
      },
      physical: {
        value: physicalConnection,
        label: 'Conexión Física',
        emoji: '🔥',
        description: 'Atracción y compatibilidad física'
      },
      spiritual: {
        value: spiritualConnection,
        label: 'Conexión Espiritual',
        emoji: '✨',
        description: 'Afinidad de valores y propósito'
      }
    },
    
    // Año personal de la pareja
    coupleYear: {
      year: currentYear,
      value: couplePersonalYear,
      meaning: getNumberMeaning(couplePersonalYear)
    },
    
    // Vibración dominante
    dominantGroup: {
      key: dominantGroupKey,
      ...dominantGroup
    },
    
    // Pináculo de Pareja (Horizontales)
    pinnacle: {
      A: { value: pinnacleA, meaning: HORIZONTALES_PAREJA[pinnacleA]?.A || null },
      B: { value: pinnacleB, meaning: HORIZONTALES_PAREJA[pinnacleB]?.B || null },
      C: { value: pinnacleC, meaning: HORIZONTALES_PAREJA[pinnacleC]?.C || null },
      D: { value: pinnacleD, meaning: HORIZONTALES_PAREJA[pinnacleD]?.D || null }
    },
    
    // Cálculo detallado (para mostrar)
    calculation: {
      day1: date1.day,
      day2: date2.day,
      sum: date1.day + date2.day,
      reduced: coupleNumber
    }
  };
}

// ============================================
// SIGNIFICADOS DE LOS NÚMEROS PERSONALES
// ============================================

export const NUMBER_MEANINGS = {
  1: {
    keyword: 'Líder',
    traits: 'Independiente, pionero, ambicioso',
    inRelationship: 'Necesita espacio para liderar y tomar decisiones. Valora la independencia dentro de la relación.'
  },
  2: {
    keyword: 'Diplomático',
    traits: 'Cooperativo, sensible, equilibrado',
    inRelationship: 'Busca armonía y paz. Es el mediador natural, necesita sentirse en pareja y apoyado.'
  },
  3: {
    keyword: 'Creativo',
    traits: 'Expresivo, optimista, comunicativo',
    inRelationship: 'Aporta alegría y creatividad. Necesita expresarse y ser escuchado.'
  },
  4: {
    keyword: 'Constructor',
    traits: 'Estable, práctico, trabajador',
    inRelationship: 'Ofrece estabilidad y seguridad. Valora el compromiso y la construcción a largo plazo.'
  },
  5: {
    keyword: 'Aventurero',
    traits: 'Libre, adaptable, curioso',
    inRelationship: 'Necesita variedad y aventura. Aporta emoción pero requiere libertad.'
  },
  6: {
    keyword: 'Protector',
    traits: 'Amoroso, responsable, familiar',
    inRelationship: 'El más romántico. Centrado en el hogar, la familia y el cuidado del otro.'
  },
  7: {
    keyword: 'Místico',
    traits: 'Analítico, espiritual, introvertido',
    inRelationship: 'Busca conexión profunda e intelectual. Necesita tiempo a solas y espacio para reflexionar.'
  },
  8: {
    keyword: 'Poderoso',
    traits: 'Ambicioso, material, ejecutivo',
    inRelationship: 'Orientado al éxito conjunto. Busca construir poder y abundancia en pareja.'
  },
  9: {
    keyword: 'Humanitario',
    traits: 'Compasivo, idealista, generoso',
    inRelationship: 'Amor universal. Puede dar mucho pero necesita aprender a recibir.'
  },
  11: {
    keyword: 'Visionario',
    traits: 'Intuitivo, inspirador, sensible (Maestro)',
    inRelationship: 'Conexión espiritual elevada. Muy sensible, busca una misión compartida.'
  },
  22: {
    keyword: 'Maestro Constructor',
    traits: 'Visionario, práctico, poderoso (Maestro)',
    inRelationship: 'Destinado a construir algo grande. Busca pareja que comparta su visión.'
  }
};

// ============================================
// DESCRIPCIONES PERSONALIZADAS POR NÚMERO
// (15+ variaciones para evitar repetición)
// ============================================

/**
 * Descripciones únicas de cómo cada número se expresa en pareja.
 * Usadas cuando dos personas son del mismo grupo para evitar texto repetido.
 */
export const PERSONAL_NUMBER_IN_RELATIONSHIP = {
  // GRUPO CEREBRAL (1, 5, 7, 11)
  1: {
    shortDesc: 'Lidera con la mente',
    inRelationship: 'Quien nace bajo el 1 necesita sentirse admirado intelectualmente. Su amor se demuestra tomando la iniciativa, proponiendo ideas y abriendo caminos. En pareja, es el estratega: planifica, decide y protege con su visión. Necesita una pareja que respete su autonomía pero que también lo desafíe mentalmente. No tolera la pasividad ni la dependencia excesiva.',
    gift: 'Aporta dirección, iniciativa y valentía para tomar decisiones difíciles.',
    needs: 'Admiración genuina, espacio para liderar, y una pareja con criterio propio.'
  },
  5: {
    shortDesc: 'Mente inquieta y libre',
    inRelationship: 'El 5 es el más libre de los números cerebrales. Su mente necesita estímulos constantes: viajes, experiencias nuevas, conversaciones que lo sorprendan. En pareja es divertido, espontáneo e impredecible. Se aburre con facilidad si cae en la rutina. Ama intensamente pero necesita sentir que no pierde su libertad. Es el aventurero mental que convierte cada día en una posibilidad.',
    gift: 'Aporta frescura, adaptabilidad y la capacidad de ver la vida como una aventura.',
    needs: 'Libertad sin culpas, variedad, y una pareja que no intente controlarlo.'
  },
  7: {
    shortDesc: 'Pensador profundo',
    inRelationship: 'El 7 es el más introspectivo y misterioso. No abre su mundo interior fácilmente, pero cuando lo hace, la conexión es profunda y única. En pareja busca calidad sobre cantidad: prefiere una conversación significativa a mil charlas superficiales. Necesita tiempo a solas para procesar, no es frialdad sino su forma de recargarse. Cuando ama, lo hace con la mente y el alma.',
    gift: 'Aporta profundidad, sabiduría y una perspectiva única sobre la vida.',
    needs: 'Espacio para reflexionar, conversaciones con sustancia, respeto a su silencio.'
  },
  11: {
    shortDesc: 'Visionario intuitivo',
    inRelationship: 'El 11 es un número maestro que percibe más allá de lo evidente. En pareja, tiene una conexión casi telepática: sabe lo que el otro siente antes de que lo diga. Es altamente sensible y puede absorber las emociones del entorno. Busca una relación con propósito, una misión compartida que trascienda lo cotidiano. Su amor es inspirador pero necesita una pareja que entienda su sensibilidad.',
    gift: 'Aporta inspiración, intuición y una conexión espiritual elevada.',
    needs: 'Comprensión de su sensibilidad, un propósito compartido, paz emocional.'
  },

  // GRUPO MATERIAL (2, 4, 8, 22)
  2: {
    shortDesc: 'Alma diplomática',
    inRelationship: 'El 2 vive para la pareja. Es el número que más naturalmente entiende el "nosotros". Su don es la empatía: percibe las necesidades del otro y busca equilibrio constantemente. En la relación es el mediador, el que suaviza conflictos y crea armonía. Puede sacrificarse demasiado si no tiene cuidado. Su amor es gentil, constante y profundamente leal.',
    gift: 'Aporta armonía, cooperación y una sensibilidad genuina hacia el otro.',
    needs: 'Sentirse valorado, reciprocidad, y una pareja que no abuse de su bondad.'
  },
  4: {
    shortDesc: 'Constructor de cimientos',
    inRelationship: 'El 4 es la roca. Cuando ama, construye: un hogar, una familia, un futuro sólido. No es el más expresivo verbalmente pero sus acciones hablan por él. Es leal hasta la médula y espera lo mismo a cambio. Puede parecer terco porque tiene claros sus valores y no los negocia. Su amor es práctico: te arregla cosas, te resuelve problemas, está ahí cuando lo necesitas.',
    gift: 'Aporta estabilidad, lealtad inquebrantable y un compromiso real.',
    needs: 'Consistencia, una pareja confiable, y que valoren sus actos de servicio.'
  },
  8: {
    shortDesc: 'Poder y abundancia',
    inRelationship: 'El 8 tiene mentalidad de abundancia. En pareja, busca construir un imperio juntos: económico, profesional, de influencia. Es protector y proveedor por naturaleza. Puede ser dominante si su pareja no tiene carácter propio. Respeta la fuerza y la ambición. Su amor se demuestra creando oportunidades, abriendo puertas y asegurando el bienestar material de los suyos.',
    gift: 'Aporta visión de éxito, capacidad ejecutiva y seguridad material.',
    needs: 'Respeto a su autoridad, una pareja con metas propias, y admiración por sus logros.'
  },
  22: {
    shortDesc: 'Maestro constructor',
    inRelationship: 'El 22 es el número maestro más poderoso. Tiene la visión del 11 pero con la capacidad práctica del 4. En pareja, sueña en grande y construye en grande. Busca a alguien que comparta su visión de dejar huella en el mundo. Puede ser intenso porque sus estándares son altos. Cuando encuentra a su pareja ideal, juntos pueden lograr cosas extraordinarias que trascienden lo personal.',
    gift: 'Aporta una visión trascendente combinada con la capacidad de materializarla.',
    needs: 'Una pareja visionaria, paciencia para sus grandes proyectos, y fe en su misión.'
  },

  // GRUPO CREATIVO (3, 6, 9)
  3: {
    shortDesc: 'Expresión y alegría',
    inRelationship: 'El 3 ilumina cualquier relación con su energía. Es comunicativo, expresivo y naturalmente optimista. En pareja necesita poder expresarse libremente: hablar, crear, compartir. El silencio lo incomoda, necesita retroalimentación constante. Su amor es divertido, ligero y lleno de detalles creativos. Puede dispersarse si tiene demasiados intereses, pero su presencia siempre suma color a la vida.',
    gift: 'Aporta alegría, comunicación fluida y una chispa creativa constante.',
    needs: 'Ser escuchado, libertad de expresión, y una pareja que celebre su creatividad.'
  },
  6: {
    shortDesc: 'Amor incondicional',
    inRelationship: 'El 6 es el número del amor puro. Nació para cuidar, proteger y crear un hogar lleno de calidez. En pareja es romántico, detallista y siempre atento a las necesidades del otro. Puede caer en el rol de "salvador" si no tiene cuidado. Su amor es nutritivo y envolvente. El hogar y la familia son sagrados para él. Cuando ama, lo hace con todo el corazón sin reservas.',
    gift: 'Aporta calidez, cuidado genuino y un amor que nutre el alma.',
    needs: 'Que valoren sus cuidados, reciprocidad emocional, y un hogar en armonía.'
  },
  9: {
    shortDesc: 'Amor universal',
    inRelationship: 'El 9 ama a la humanidad, y eso incluye a su pareja pero no se limita a ella. Tiene una perspectiva amplia: ve el mundo, las causas, el bien mayor. En pareja es generoso, compasivo y profundamente empático. Puede dar tanto que se olvida de recibir. Su amor trasciende lo personal y busca significado. Necesita una pareja que entienda que su corazón es grande y tiene espacio para muchos.',
    gift: 'Aporta compasión, generosidad y una perspectiva elevada del amor.',
    needs: 'Espacio para sus causas, una pareja independiente, y aprender a recibir.'
  }
};

/**
 * Variaciones adicionales para cuando ambos tienen el MISMO número personal.
 * Se selecciona basándose en quién es persona1 vs persona2.
 */
export const SAME_NUMBER_VARIATIONS = {
  1: [
    'Como líder nato, busca admiración y espacio para brillar. Su forma de amar es tomar la iniciativa.',
    'Pionero por naturaleza, necesita sentir que su pareja confía en sus decisiones y visión.',
    'Independiente y directo, demuestra amor abriendo caminos y resolviendo obstáculos.'
  ],
  2: [
    'Sensible y empático, su mayor don es crear armonía y entender las emociones del otro.',
    'Cooperativo por esencia, encuentra felicidad cuando la relación fluye en equilibrio.',
    'Diplomático natural, su amor se expresa en pequeños gestos de cuidado y atención.'
  ],
  3: [
    'Expresivo y optimista, necesita compartir su mundo interior a través de palabras y creatividad.',
    'Comunicador nato, su amor florece cuando puede expresarse libremente sin juicios.',
    'Alegre y sociable, aporta luz y diversión a cada momento de la relación.'
  ],
  4: [
    'Sólido y confiable, su amor se demuestra construyendo un futuro seguro paso a paso.',
    'Práctico y leal, prefiere las acciones sobre las palabras para expresar lo que siente.',
    'Trabajador incansable, su forma de amar es asegurarse de que nada falte.'
  ],
  5: [
    'Libre y aventurero, su amor necesita espacio para respirar y experiencias nuevas.',
    'Curioso por naturaleza, mantiene la relación viva con ideas frescas y espontaneidad.',
    'Adaptable y dinámico, su mayor miedo es la rutina que apaga su chispa vital.'
  ],
  6: [
    'Amoroso y protector, encuentra su propósito cuidando y nutriendo a quienes ama.',
    'Romántico de corazón, crea un hogar lleno de calidez y detalles significativos.',
    'Responsable y devoto, su amor es un refugio seguro en medio de cualquier tormenta.'
  ],
  7: [
    'Profundo y reflexivo, su conexión más fuerte ocurre en el plano mental y espiritual.',
    'Introspectivo y sabio, necesita momentos de silencio para procesar y recargarse.',
    'Analítico y misterioso, abre su mundo interior solo a quien demuestra ser digno.'
  ],
  8: [
    'Ambicioso y protector, su amor se traduce en crear abundancia y seguridad para los suyos.',
    'Ejecutivo por naturaleza, busca construir un legado junto a su pareja.',
    'Poderoso y determinado, respeta la fuerza y espera una pareja con metas propias.'
  ],
  9: [
    'Compasivo y generoso, su amor trasciende lo personal y abraza causas más grandes.',
    'Idealista de corazón, busca significado profundo en cada aspecto de la relación.',
    'Humanitario por esencia, puede dar tanto que a veces olvida recibir.'
  ],
  11: [
    'Intuitivo y visionario, percibe las emociones antes de que sean expresadas.',
    'Sensible y espiritual, busca una conexión que trascienda lo mundano.',
    'Inspirador por naturaleza, su amor eleva y transforma a quien está a su lado.'
  ],
  22: [
    'Visionario y práctico, sueña en grande y tiene la capacidad de materializar esos sueños.',
    'Constructor maestro, busca una pareja que comparta su misión de dejar huella.',
    'Poderoso y trascendente, su amor está destinado a crear algo que perdure.'
  ]
};

/**
 * Obtiene la descripción personalizada de un número en pareja
 */
export const getPersonalNumberInRelationship = (num) => {
  return PERSONAL_NUMBER_IN_RELATIONSHIP[num] || PERSONAL_NUMBER_IN_RELATIONSHIP[reduceNumber(num)] || null;
};

/**
 * Obtiene una variación de texto para cuando ambos tienen el mismo número
 * @param {number} num - El número personal
 * @param {number} index - 0 para persona1, 1 para persona2
 */
export const getSameNumberVariation = (num, index = 0) => {
  const variations = SAME_NUMBER_VARIATIONS[num] || SAME_NUMBER_VARIATIONS[reduceNumber(num)];
  if (!variations) return null;
  return variations[index % variations.length];
};

/**
 * Obtiene el significado de un número personal
 */
export const getNumberMeaning = (num) => {
  return NUMBER_MEANINGS[num] || NUMBER_MEANINGS[reduceNumber(num)] || {
    keyword: 'Desconocido',
    traits: '',
    inRelationship: ''
  };
};

// ============================================
// HORIZONTALES DE PAREJA (Pináculo A, B, C, D)
// ============================================

/**
 * Significados por número y posición del Pináculo de Pareja.
 * 
 * Posiciones:
 *   A (Karma)    = reduceNumber(mes1 + mes2)
 *   B (Pareja)   = reduceNumber(día1 + día2)
 *   C (Antídoto) = reduceNumber(año1 + año2)
 *   D (Imagen)   = reduceNumber(A + B + C)
 * 
 * Fuente: Horizontales de Pareja
 */
export const HORIZONTALES_PAREJA = {
  1: {
    A: {
      title: 'Karma 1',
      text: 'Lección de apego vs. amor: miedo a perderse/abandonar lleva a codependencia, celos o frialdad. Clave: sostener identidad sin controlar.',
      keywords: ['independencia', 'autoafirmación', 'límites', 'control']
    },
    B: {
      title: 'Pareja 1',
      text: 'Unión dinámica, aventurera e innovadora; atracción rápida y mucha energía. Aprendizaje: compartir poder y bajar la lucha de egos para evitar dependencia.',
      keywords: ['liderazgo', 'movimiento', 'ego', 'acuerdos']
    },
    C: {
      title: 'Antídoto 1',
      text: 'Amar con libertad: respetar la esencia del otro y no intentar "cambiarlo". Equilibrio entre pasión y autonomía, con distancia afectiva sana.',
      keywords: ['respeto', 'libertad', 'identidad', 'equilibrio']
    },
    D: {
      title: 'Imagen 1',
      text: 'Se les percibe rebeldes/modernos: o los admiran o los rechazan; proyectan éxito y empuje. Pareja independiente, poco convencional, rápida para actuar.',
      keywords: ['vanguardia', 'confianza', 'logros', 'impulso']
    }
  },
  2: {
    A: {
      title: 'Karma 2',
      text: 'Trabajo de vínculo seguro: miedo al abandono activa demanda emocional, sumisión o idealización. La tarea es equilibrar dar/recibir sin perder autonomía.',
      keywords: ['seguridad', 'pertenencia', 'dependencia', 'equilibrio']
    },
    B: {
      title: 'Pareja 2',
      text: 'Unión romántica, estable y solidaria; disfrutan lo doméstico y servir a otros. Aprendizaje: no ahogarse en sentimentalismo/manipulación ni perder individualidad.',
      keywords: ['armonía', 'servicio', 'honestidad', 'límites']
    },
    C: {
      title: 'Antídoto 2',
      text: 'Fortalece el "nosotros": apoyo mutuo, empatía y compromiso; lazo afectivo muy fuerte. Clave: sostener inclusión sin exagerar drama o exclusiones.',
      keywords: ['pertenencia', 'empatía', 'compromiso', 'inclusión']
    },
    D: {
      title: 'Imagen 2',
      text: 'Se les ve tranquilos, serviciales y amorosos; hacen amigos fácil y evitan confrontación. Proyectan hogar/armonía y vocación humanitaria.',
      keywords: ['paz', 'solidaridad', 'sociabilidad', 'calma']
    }
  },
  3: {
    A: {
      title: 'Karma 3',
      text: 'Lección de comunicación e intimidad: riesgo de superficialidad, malos entendidos y roles Padre–Niño. Tarea: hablar "adulto a adulto" y construir compromiso real.',
      keywords: ['comunicación', 'intimidad', 'madurez', 'claridad']
    },
    B: {
      title: 'Pareja 3',
      text: 'Conexión por conversación estimulante; relación alegre, sociable, creativa y con mucha química. Aprendizaje: enfocarse y cumplir compromisos, con diplomacia en crisis.',
      keywords: ['carisma', 'diversión', 'riesgo', 'enfoque']
    },
    C: {
      title: 'Antídoto 3',
      text: 'Se sostiene por alegría, viajes, creatividad y erotismo; "dos adolescentes enamorados" que disfrutan la vida. Clave: equilibrar libertad con autosostenimiento y realismo.',
      keywords: ['goce', 'creatividad', 'escapadas', 'responsabilidad']
    },
    D: {
      title: 'Imagen 3',
      text: 'Pareja magnética y expresiva; se les ve multitalentosos, populares y mediadores del grupo. Proyectan belleza, facilidad de palabra e influencia social.',
      keywords: ['magnetismo', 'popularidad', 'comunicación', 'brillo']
    }
  },
  4: {
    A: {
      title: 'Karma 4',
      text: 'Aprendizaje de estructura y compromiso: construir seguridad sin rigidez, control ni competencia interna. Tarea: bases propias (trabajo, constancia) y flexibilidad.',
      keywords: ['estabilidad', 'esfuerzo', 'disciplina', 'flexibilidad']
    },
    B: {
      title: 'Pareja 4',
      text: 'Combinación muy estable y realista; enfoque práctico, hogar, cimientos y seguridad material. Aprendizaje: abrirse a cambios y no caer en posesión/control.',
      keywords: ['hogar', 'constancia', 'realismo', 'adaptación']
    },
    C: {
      title: 'Antídoto 4',
      text: 'Se activa con metas, disciplina y respeto por los logros del otro; se sienten "piedra" de sostén mutuo. Cuidado: no encerrarse, autoritarismo y miedo a cambiar.',
      keywords: ['metas', 'arraigo', 'orden', 'apertura']
    },
    D: {
      title: 'Imagen 4',
      text: 'Proyectan solidez, formalidad y prosperidad; suelen ser organizadores y ejemplo de vida estructurada. Se les valora por jerarquías, método y confiabilidad.',
      keywords: ['solidez', 'prestigio', 'método', 'confianza']
    }
  },
  5: {
    A: {
      title: 'Karma 5',
      text: 'Lección de aceptación y adaptabilidad: puede volverse ring de pelea, huida y dinámica sexual como "pegamento" o poder. Tarea: cambiar reacción por respuesta y cortar maltrato.',
      keywords: ['libertad', 'impulsividad', 'conflicto', 'conciencia']
    },
    B: {
      title: 'Pareja 5',
      text: 'Aventureros, pasionales y sociables; mucha variedad, coqueteo y compatibilidad sexual. Aprendizaje: evitar superficialidad y sostener estabilidad/compromiso para durar.',
      keywords: ['aventura', 'pasión', 'cambio', 'compromiso']
    },
    C: {
      title: 'Antídoto 5',
      text: 'Se activa con flexibilidad, respeto a la libertad y planes expansivos (viajes, cambios, aprendizaje). Mantener rutinas vivas y progresistas sin ataduras impuestas.',
      keywords: ['tolerancia', 'progreso', 'exploración', 'desapego']
    },
    D: {
      title: 'Imagen 5',
      text: 'Se les ve rebeldes y magnéticos; curiosos, adelantados a su tiempo y enemigos de la rutina. Proyectan carisma, innovación y movimiento constante.',
      keywords: ['rebeldía', 'magnetismo', 'versatilidad', 'prisa']
    }
  },
  6: {
    A: {
      title: 'Karma 6',
      text: 'Lección de nutrición emocional y amor propio: riesgo de sobreprotección, celos, sacrificio y demandas ("te doy todo, me debes"). Tarea: confianza, límites y vínculo-refugio.',
      keywords: ['familia', 'seguridad', 'merecimiento', 'límites']
    },
    B: {
      title: 'Pareja 6',
      text: 'Relación armoniosa, tierna y comprometida; buscan paz y futuro estable. Aprendizaje: no asfixiar con control, crear intereses externos y espacios personales.',
      keywords: ['ternura', 'estabilidad', 'justicia', 'aire']
    },
    C: {
      title: 'Antídoto 6',
      text: 'Se activa con cariño, solidaridad y detalles; disfrutan hogar, familia y afecto constante. Clave: diferenciar servir vs. volverse servil y respetar tiempos del otro.',
      keywords: ['apoyo', 'empatía', 'romance', 'cuidado']
    },
    D: {
      title: 'Imagen 6',
      text: 'Pareja agradable e integradora; buenos anfitriones, queridos y protectores, con gusto por confort y armonía. Cuidado: celos y falta de límites con familia/amigos.',
      keywords: ['calidez', 'armonía', 'familia', 'límites']
    }
  },
  7: {
    A: {
      title: 'Karma 7',
      text: 'Lección de libertad vs. exceso de exigencia: sacrificio, autoengaño y control que vuelve "prisioneros" del vínculo. Tarea: no invadir, soltar culpa y recuperar proyecto propio.',
      keywords: ['respeto', 'libertad', 'límites', 'responsabilidad']
    },
    B: {
      title: 'Pareja 7',
      text: 'Unión seria, leal y responsable; valores compartidos y profundidad sensorial. Aprendizaje: meter diversión/erotismo, evitar obsesión y repartir cargas sin sobre-responsabilizarse.',
      keywords: ['compromiso', 'lealtad', 'rutina', 'balance']
    },
    C: {
      title: 'Antídoto 7',
      text: 'Se fortalece con reflexión, privacidad, estudio y diálogo paciente; respeto por espacios y profundidad mental. Clave: no cargar a otros (familia/demandas) y priorizar sueños propios.',
      keywords: ['introspección', 'sabiduría', 'espacio', 'prioridades']
    },
    D: {
      title: 'Imagen 7',
      text: 'Se les percibe analíticos, intuitivos y "almas gemelas"; muy conectados, reservados y maduros. Proyectan brillo sutil, curiosidad intelectual y cautela al decidir.',
      keywords: ['complicidad', 'misterio', 'inteligencia', 'conexión']
    }
  },
  8: {
    A: {
      title: 'Karma 8',
      text: 'Lección de poder/estatus: riesgo de lucha de egos y control por seguridad material. Tarea: metas comunes, trabajo propio y no usar dinero/imagen como sustituto del amor.',
      keywords: ['poder', 'seguridad', 'ambición', 'cooperación']
    },
    B: {
      title: 'Pareja 8',
      text: 'Diferentes pero altamente exitosos si aceptan temperamentos y alinean propósito; vienen a materializar logros. Aprendizaje: comunicar sentimientos y dejar la batalla por liderazgo.',
      keywords: ['propósito', 'resultados', 'respeto', 'comunicación']
    },
    C: {
      title: 'Antídoto 8',
      text: 'Se activa con admiración mutua y empoderamiento; apoyar el éxito del otro crea solidez. Clave: objetivos compartidos + diálogo sincero para evitar guerra de poder.',
      keywords: ['admiración', 'metas', 'éxito', 'alianza']
    },
    D: {
      title: 'Imagen 8',
      text: 'Proyectan autoridad, disciplina y "Rey Midas"; líderes temidos/admiran, orientados a resultados. Exigen mucho y sostienen al grupo con seguridad y control.',
      keywords: ['autoridad', 'disciplina', 'éxito', 'control']
    }
  },
  9: {
    A: {
      title: 'Karma 9',
      text: 'Lección de apego/validación: búsqueda de reconocimiento, perfeccionismo y control; miedo a no estar incluido. Tarea: transformar ego en servicio y misión humana compartida.',
      keywords: ['trascendencia', 'compasión', 'reconocimiento', 'misión']
    },
    B: {
      title: 'Pareja 9',
      text: 'Unión idealista, sensible y filosófica; tendencia a "salvar el mundo" y brillar socialmente. Aprendizaje: espacios personales y bajar dramatismo/control por inseguridad afectiva.',
      keywords: ['ideales', 'cultura', 'compañerismo', 'libertad']
    },
    C: {
      title: 'Antídoto 9',
      text: 'Se sana con amor universal, generosidad y propósito social; igualdad y solidaridad como base. Clave: cortar lazos que anulen individualidad y madurar hacia autosuficiencia.',
      keywords: ['altruismo', 'propósito', 'igualdad', 'madurez']
    },
    D: {
      title: 'Imagen 9',
      text: 'Se les ve serviciales e inspiradores; calidez humana, talento y vocación de ayuda, incluso fama. Riesgo: frialdad si no se cumplen expectativas; independencia alta.',
      keywords: ['servicio', 'inspiración', 'idealismo', 'desapego']
    }
  },
  11: {
    A: {
      title: 'Karma 11',
      text: 'Lección de libertad vs. sacrificio: control, humillación y autoanulación por "aguantar" para ser valorado. Tarea: límites, responsabilidad propia y salir de la culpa/autoengaño.',
      keywords: ['dignidad', 'libertad', 'límites', 'autocuidado']
    },
    B: {
      title: 'Pareja 11',
      text: 'Atracción intensa y conexión sutil (casi "telepática"); mezcla de erotismo y espiritualidad. Aprendizaje: no dar por hecho, hablar claro y evitar evasión/adicciones.',
      keywords: ['intuición', 'comunicación', 'fantasía', 'honestidad']
    },
    C: {
      title: 'Antídoto 11',
      text: 'Se activa con proyectos de consciencia/enseñanza (educación, ciencia, espiritualidad) y mejora mutua constante. Clave: dialogar con profundidad y no cargar responsabilidades ajenas.',
      keywords: ['propósito', 'estudio', 'reflexión', 'crecimiento']
    },
    D: {
      title: 'Imagen 11',
      text: 'Pareja "modelo/influencer": elegante, reservada y misteriosa; se les admira por madurez y sabiduría. Proyectan ética, análisis y servicio al colectivo.',
      keywords: ['ejemplaridad', 'enigma', 'intelecto', 'guía']
    }
  },
  22: {
    A: {
      title: 'Karma 22',
      text: 'Lección de construcción y seguridad: riesgo de rigidez, control y foco excesivo en estatus/dinero. Tarea: metas compartidas con propósito y no perder el sentido emocional del vínculo.',
      keywords: ['estructura', 'poder', 'seguridad', 'propósito']
    },
    B: {
      title: 'Pareja 22',
      text: 'Equipo triunfador: ambición, productividad y gran potencia para materializar futuro estable. Aprendizaje: evitar batalla de egos, sostener comunicación diaria y disfrutar el presente.',
      keywords: ['logro', 'disciplina', 'liderazgo', 'disfrute']
    },
    C: {
      title: 'Antídoto 22',
      text: 'Invitación a culminar "obras" para el colectivo: crear herramientas de abundancia con voluntad y estrategia. Clave: atender lo emocional y resolver problemas volviendo a la visión común.',
      keywords: ['legado', 'creación', 'abundancia', 'estrategia']
    },
    D: {
      title: 'Imagen 22',
      text: 'Proyectan autoridad y éxito de alto impacto social; líderes que inspiran por resultados y disciplina. Cuidado: ambición/avaricia puede erosionar el vínculo y su propósito.',
      keywords: ['poder', 'impacto', 'prosperidad', 'responsabilidad']
    }
  }
};

/**
 * Metadatos de las posiciones del Pináculo de Pareja
 */
export const PINNACLE_POSITIONS = {
  A: { label: 'Karma', emoji: '🔥', color: 'rose', description: 'Tema kármico de la pareja: la herida que vienen a sanar juntos.' },
  B: { label: 'Pareja', emoji: '💜', color: 'purple', description: 'La dinámica natural de la relación: fortalezas y aprendizajes.' },
  C: { label: 'Antídoto', emoji: '💚', color: 'emerald', description: 'La cualidad sanadora: lo que equilibra y fortalece la unión.' },
  D: { label: 'Imagen', emoji: '👁️', color: 'indigo', description: 'Cómo los percibe el mundo: la proyección social de la pareja.' }
};

/**
 * Obtiene el significado de un número para una posición del Pináculo
 */
export const getHorizontalMeaning = (number, position) => {
  const numData = HORIZONTALES_PAREJA[number];
  if (!numData) return null;
  return numData[position] || null;
};
